import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, Utensils, Save, RefreshCw, CheckCircle, Flame, Target, Scale, Lock, Trophy, Info, ChevronUp, ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { FOOD_DATABASE } from '../data/foodDatabase';
import {
  calculateProteinPortion,
  calculateCarbPortion,
  calculatePortionForSelect,
  filterFoodsByPreferences,
  roundGramsForDisplay,
  getFoodById
} from '../utils/nutritionCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GOAL_LABELS = {
  'perder': { label: 'Pérdida de Grasa', icon: Flame, color: 'text-orange-500', bg: 'bg-orange-500/20' },
  'ganar': { label: 'Aumento de Masa', icon: Target, color: 'text-green-500', bg: 'bg-green-500/20' },
  'mantener': { label: 'Mantener Peso', icon: Scale, color: 'text-blue-500', bg: 'bg-blue-500/20' }
};

/**
 * Editor de gramos: input numérico + botones -5/+5.
 * Definido fuera del componente padre para evitar remount y perder foco al escribir.
 */
import GramEditor from '../components/GramEditor';

const ChallengeNutrition = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [step, setStep] = useState(1);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [meals, setMeals] = useState([]);
  const [savedPlan, setSavedPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSavedPlan, setShowSavedPlan] = useState(false);
  
  const [formData, setFormData] = useState({
    peso: '',
    estatura: '',
    edad: '',
    sexo: 'mujer',
    nivel_actividad: 'moderado',
    numero_comidas: '4',
    comida_post_entreno: '', // '' = ninguna, '1'..'6' = índice 1-based de la comida
    alergias: '',
    alimentos_no_gustan: ''
  });

  useEffect(() => {
    fetchData();
  }, [challengeId]);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      // Obtener datos del challenge
      const challengeRes = await axios.get(`${BACKEND_URL}/api/challenges/${challengeId}`);
      setChallenge(challengeRes.data);

      // Obtener formulario guardado si existe
      try {
        const savedRes = await axios.get(
          `${BACKEND_URL}/api/challenges/${challengeId}/nutrition-form`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (savedRes.data && savedRes.data.form_data) {
          setSavedPlan(savedRes.data);
          setShowSavedPlan(true);
        }
      } catch (err) {
        // No hay formulario guardado
      }
    } catch (error) {
      toast.error('Error al cargar datos');
      navigate('/challenges');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar alimentos según preferencias
  const filterFoods = (foods) => {
    return filterFoodsByPreferences(foods, formData.alergias, formData.alimentos_no_gustan);
  };

  /**
   * Redistribuye la grasa DIARIA restante entre las comidas NO post-entreno.
   * Descuenta la grasa que ya aportan las proteínas seleccionadas de la meta diaria,
   * y reparte lo que queda entre las comidas no-PW. Esto evita que el sistema pida
   * grasa adicional cuando las proteínas ya cubrieron (o excedieron) la meta diaria.
   */
  /**
   * AUTO-BALANCE TOTAL: recalcula los gramos EXACTOS de cada comida
   * para que la SUMA total del día = meta_diaria exacta.
   *
   * Estrategia:
   *  1) Grasa: descuenta la grasa aportada por las proteínas seleccionadas.
   *     Distribuye la grasa restante entre las comidas NO post-entreno.
   *     Si una proteína ya excede su cuota → grasa de esa comida = 0 (deshabilitada).
   *  2) Proteína: recalcula gramos = meta_P_por_comida / prot_por_100g.
   *  3) Carbohidrato: descuenta los carbos aportados por proteína + fruta,
   *     luego calcula gramos = remaining / carb_por_100g.
   *  4) Normalización final: si aún así el total excede la meta (por ej. proteínas grasas
   *     como huevo entero), escala proporcionalmente los gramos para llegar a meta EXACTA.
   */
  const autoBalanceAllMacros = (mealsList, plan) => {
    if (!plan) return mealsList;

    let workMeals = [...mealsList];

    // === PASO 1: Grasa ===
    let fatFromProteins = 0;
    workMeals.forEach(m => {
      const p = getFoodById('proteina', m.foods.proteina.foodId);
      if (p && m.foods.proteina.grams > 0) {
        fatFromProteins += (p.grasas * m.foods.proteina.grams) / 100;
      }
    });

    const nonPWCount = workMeals.filter(m => !m.isPostWorkout).length;
    const remainingFat = Math.max(plan.grasas - fatFromProteins, 0);
    const fatPerNonPW = nonPWCount > 0 ? remainingFat / nonPWCount : 0;

    workMeals = workMeals.map(m => {
      if (m.isPostWorkout) {
        return {
          ...m,
          targets: { ...m.targets, grasas: 0 },
          foods: {
            ...m.foods,
            grasa: { foodId: '', grams: 0, disabled: true, message: 'Comida post-entreno: sin grasa' }
          }
        };
      }
      const newTarget = Math.round(fatPerNonPW);
      const proteinFood = getFoodById('proteina', m.foods.proteina.foodId);
      const fatFromProt = proteinFood && m.foods.proteina.grams > 0
        ? (proteinFood.grasas * m.foods.proteina.grams) / 100
        : 0;
      if (fatFromProt >= newTarget) {
        return {
          ...m,
          targets: { ...m.targets, grasas: newTarget },
          foods: {
            ...m.foods,
            grasa: { foodId: '', grams: 0, disabled: true, message: 'La proteína aporta suficiente grasa' }
          }
        };
      }
      const remainingForMeal = Math.max(newTarget - fatFromProt, 0);
      const fatFood = getFoodById('grasa', m.foods.grasa.foodId);
      if (fatFood && remainingForMeal > 0) {
        const grams = fatFood.grasas > 0 ? Math.round((remainingForMeal / fatFood.grasas) * 100) : 0;
        return {
          ...m,
          targets: { ...m.targets, grasas: newTarget },
          foods: { ...m.foods, grasa: { ...m.foods.grasa, grams, disabled: false, message: null } }
        };
      }
      return {
        ...m,
        targets: { ...m.targets, grasas: newTarget },
        foods: { ...m.foods, grasa: { ...m.foods.grasa, disabled: false, message: null } }
      };
    });

    // === PASO 2: Proteína - recalcular gramos exactos ===
    const mealsWithProt = workMeals.filter(m => m.foods.proteina.foodId);
    const protPerMeal = mealsWithProt.length > 0 ? plan.proteinas / mealsWithProt.length : 0;
    workMeals = workMeals.map(m => {
      const food = getFoodById('proteina', m.foods.proteina.foodId);
      if (food && food.proteinas > 0) {
        const grams = Math.round((protPerMeal / food.proteinas) * 100);
        return { ...m, foods: { ...m.foods, proteina: { ...m.foods.proteina, grams } } };
      }
      return m;
    });

    // === PASO 3: Carbohidrato - descontar carbos de proteína y fruta ===
    // Primero recalcular fruta a ~15g carbos (si existe)
    workMeals = workMeals.map(m => {
      if (m.hasFruit && m.fruit.foodId) {
        const fruitFood = getFoodById('fruta', m.fruit.foodId);
        if (fruitFood && fruitFood.carbohidratos > 0) {
          const grams = Math.round((15 / fruitFood.carbohidratos) * 100);
          return { ...m, fruit: { ...m.fruit, grams } };
        }
      }
      return m;
    });

    // Carbos totales aportados por proteínas + frutas
    let carbsFromOthers = 0;
    workMeals.forEach(m => {
      const p = getFoodById('proteina', m.foods.proteina.foodId);
      if (p && m.foods.proteina.grams > 0) {
        carbsFromOthers += (p.carbohidratos * m.foods.proteina.grams) / 100;
      }
      if (m.hasFruit && m.fruit.foodId) {
        const fr = getFoodById('fruta', m.fruit.foodId);
        if (fr && m.fruit.grams > 0) {
          carbsFromOthers += (fr.carbohidratos * m.fruit.grams) / 100;
        }
      }
    });

    const mealsWithCarb = workMeals.filter(m => m.foods.carbohidrato.foodId);
    const remainingCarbs = Math.max(plan.carbohidratos - carbsFromOthers, 0);
    const carbPerMeal = mealsWithCarb.length > 0 ? remainingCarbs / mealsWithCarb.length : 0;
    workMeals = workMeals.map(m => {
      const food = getFoodById('carbohidrato', m.foods.carbohidrato.foodId);
      if (food && food.carbohidratos > 0) {
        const grams = Math.round((carbPerMeal / food.carbohidratos) * 100);
        return { ...m, foods: { ...m.foods, carbohidrato: { ...m.foods.carbohidrato, grams } } };
      }
      return m;
    });

    // === PASO 4: Normalización final por error de redondeo ===
    // Recalcular totales reales y escalar gramos si algún macro excede su meta
    const totals = { p: 0, c: 0, g: 0 };
    workMeals.forEach(m => {
      ['proteina', 'carbohidrato', 'grasa'].forEach(cat => {
        const food = getFoodById(cat, m.foods[cat].foodId);
        if (food && m.foods[cat].grams > 0) {
          const mult = m.foods[cat].grams / 100;
          totals.p += food.proteinas * mult;
          totals.c += food.carbohidratos * mult;
          totals.g += food.grasas * mult;
        }
      });
      if (m.hasFruit && m.fruit.foodId) {
        const food = getFoodById('fruta', m.fruit.foodId);
        if (food && m.fruit.grams > 0) {
          const mult = m.fruit.grams / 100;
          totals.p += food.proteinas * mult;
          totals.c += food.carbohidratos * mult;
          totals.g += food.grasas * mult;
        }
      }
    });

    // Si la GRASA total excede la meta (por proteínas grasas tipo huevo entero),
    // reducir proporcionalmente los gramos de proteína para respetar la meta de grasa
    if (totals.g > plan.grasas && plan.grasas > 0) {
      // Cuánto excede
      const fatExcess = totals.g - plan.grasas;
      // Grasa aportada por proteínas
      let fatFromProt = 0;
      workMeals.forEach(m => {
        const p = getFoodById('proteina', m.foods.proteina.foodId);
        if (p && m.foods.proteina.grams > 0) {
          fatFromProt += (p.grasas * m.foods.proteina.grams) / 100;
        }
      });
      if (fatFromProt > 0) {
        // Factor: reducir la proteína lo justo para que la grasa quede en meta
        const factor = Math.max((fatFromProt - fatExcess) / fatFromProt, 0.5); // no bajar más del 50%
        workMeals = workMeals.map(m => {
          if (m.foods.proteina.grams > 0) {
            return {
              ...m,
              foods: {
                ...m.foods,
                proteina: { ...m.foods.proteina, grams: Math.max(Math.round(m.foods.proteina.grams * factor), 20) }
              }
            };
          }
          return m;
        });
        // Recalcular totales para escalado siguiente
        totals.p = 0; totals.c = 0; totals.g = 0;
        workMeals.forEach(m => {
          ['proteina', 'carbohidrato', 'grasa'].forEach(cat => {
            const food = getFoodById(cat, m.foods[cat].foodId);
            if (food && m.foods[cat].grams > 0) {
              const mult = m.foods[cat].grams / 100;
              totals.p += food.proteinas * mult;
              totals.c += food.carbohidratos * mult;
              totals.g += food.grasas * mult;
            }
          });
          if (m.hasFruit && m.fruit.foodId) {
            const food = getFoodById('fruta', m.fruit.foodId);
            if (food && m.fruit.grams > 0) {
              const mult = m.fruit.grams / 100;
              totals.p += food.proteinas * mult;
              totals.c += food.carbohidratos * mult;
              totals.g += food.grasas * mult;
            }
          }
        });
      }
    }

    // Escalar carbo si excede
    if (totals.c > plan.carbohidratos && plan.carbohidratos > 0) {
      const factor = plan.carbohidratos / totals.c;
      workMeals = workMeals.map(m => {
        const upd = { ...m, foods: { ...m.foods } };
        if (upd.foods.carbohidrato.grams > 0) {
          upd.foods.carbohidrato = { ...upd.foods.carbohidrato, grams: Math.max(Math.round(upd.foods.carbohidrato.grams * factor), 0) };
        }
        return upd;
      });
    }

    return workMeals;
  };

  // Alias retro-compat: acepta 1 o 2 args (segundo era daily fat target, ahora ignorado)
  const rebalanceFatAcrossMeals = (mealsList) => autoBalanceAllMacros(mealsList, nutritionPlan);

  /**
   * Reordena una comida en la lista (subir/bajar).
   * Todo el objeto viaja (isPostWorkout, hasFruit, foods) con la comida.
   * Después del swap se re-balancea la grasa por si cambia numeración.
   */
  const moveMeal = (index, direction) => {
    setMeals(prev => {
      const targetIdx = index + direction;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const newMeals = [...prev];
      [newMeals[index], newMeals[targetIdx]] = [newMeals[targetIdx], newMeals[index]];
      // Actualizar nombres visibles (Comida 1, Comida 2...) según nuevo orden
      const relabelled = newMeals.map((m, i) => {
        const baseName = m.name.replace(/^Comida \d+ · /, '').replace(/^Comida \d+$/, `Comida ${i + 1}`);
        // Mantener sufijo (Post-Entreno)
        return { ...m, name: baseName.startsWith('Comida ') ? `Comida ${i + 1}` : baseName };
      });
      return nutritionPlan ? relabelled : relabelled;
    });
  };

  /**
   * Actualiza los gramos de un macro específico de una comida SIN auto-balancear.
   * Respeta la decisión manual del usuario.
   */
  const setMealFoodGrams = (mealIndex, category, newGrams) => {
    const value = Math.max(parseInt(newGrams) || 0, 0);
    setMeals(prev => prev.map((m, i) => {
      if (i !== mealIndex) return m;
      if (category === 'fruta') {
        return { ...m, fruit: { ...m.fruit, grams: value } };
      }
      return {
        ...m,
        foods: { ...m.foods, [category]: { ...m.foods[category], grams: value } }
      };
    }));
  };

  const adjustMealFoodGrams = (mealIndex, category, delta) => {
    setMeals(prev => prev.map((m, i) => {
      if (i !== mealIndex) return m;
      if (category === 'fruta') {
        const g = Math.max((m.fruit.grams || 0) + delta, 0);
        return { ...m, fruit: { ...m.fruit, grams: g } };
      }
      const cur = m.foods[category].grams || 0;
      const g = Math.max(cur + delta, 0);
      return { ...m, foods: { ...m.foods, [category]: { ...m.foods[category], grams: g } } };
    }));
  };

  /**
   * Mueve un macro (grasa o fruta) de una comida origen a una comida destino.
   * Reemplaza lo que existía en el destino con la selección del origen.
   * Después del cambio, si es grasa se re-balancea.
   */
  const moveFoodToMeal = (fromIdx, category, toIdx) => {
    if (fromIdx === toIdx) return;
    setMeals(prev => {
      if (toIdx < 0 || toIdx >= prev.length) return prev;
      const source = prev[fromIdx];
      const dest = prev[toIdx];

      // Bloquear mover grasa a una comida post-entreno
      if (category === 'grasa' && dest.isPostWorkout) {
        toast.error('No puedes mover grasa a una comida post-entreno');
        return prev;
      }

      const newMeals = [...prev];
      if (category === 'grasa') {
        const srcGrasa = source.foods.grasa;
        newMeals[fromIdx] = {
          ...source,
          foods: {
            ...source.foods,
            grasa: { foodId: '', grams: 0, disabled: source.foods.grasa.disabled, message: source.foods.grasa.message }
          }
        };
        newMeals[toIdx] = {
          ...dest,
          foods: {
            ...dest.foods,
            grasa: { foodId: srcGrasa.foodId, grams: srcGrasa.grams, disabled: false, message: null }
          }
        };
      } else if (category === 'fruta') {
        const srcFruit = source.fruit;
        newMeals[fromIdx] = { ...source, hasFruit: false, fruit: { foodId: '', grams: 0 } };
        newMeals[toIdx] = { ...dest, hasFruit: true, fruit: { foodId: srcFruit.foodId, grams: srcFruit.grams } };
      }

      return newMeals;
    });
    toast.success('Movido a la comida seleccionada');
  };

  /**
   * Alterna la presencia de vegetales en una comida.
   */
  const toggleVegetables = (mealIndex) => {
    setMeals(prev => prev.map((m, i) => i === mealIndex ? { ...m, hasVegetables: !m.hasVegetables } : m));
  };

  const calculateNutrition = () => {
    const { peso, estatura, edad, sexo, nivel_actividad, numero_comidas } = formData;
    
    if (!peso || !estatura || !edad) {
      toast.error('Completa todos los campos');
      return;
    }

    // Calcular TMB (Mifflin-St Jeor)
    let tmb;
    if (sexo === 'mujer') {
      tmb = (10 * parseFloat(peso)) + (6.25 * parseFloat(estatura)) - (5 * parseFloat(edad)) - 161;
    } else {
      tmb = (10 * parseFloat(peso)) + (6.25 * parseFloat(estatura)) - (5 * parseFloat(edad)) + 5;
    }

    // Factor de actividad
    const factores = {
      sedentario: 1.2,
      ligero: 1.375,
      moderado: 1.55,
      activo: 1.725,
      muy_activo: 1.9
    };
    
    const mantenimiento = Math.round(tmb * factores[nivel_actividad]);
    
    // Ajustar según objetivo del challenge
    const objetivo = challenge?.nutrition_goal || 'mantener';
    let calorias;
    if (objetivo === 'perder') {
      calorias = Math.round(mantenimiento * 0.8);
    } else if (objetivo === 'ganar') {
      calorias = Math.round(mantenimiento * 1.15);
    } else {
      calorias = mantenimiento;
    }

    // Calcular macros totales
    const proteinas = Math.round((calorias * 0.30) / 4);
    const carbohidratos = Math.round((calorias * 0.45) / 4);
    const grasas = Math.round((calorias * 0.25) / 9);

    const numMeals = parseInt(numero_comidas);
    
    // Distribuir macros por comida
    const protPerMeal = Math.round(proteinas / numMeals);
    const carbPerMeal = Math.round(carbohidratos / numMeals);

    // Determinar cuál comida es post-entreno (1-based -> 0-based)
    const postWorkoutIdx = formData.comida_post_entreno
      ? parseInt(formData.comida_post_entreno) - 1
      : -1;
    // Cantidad de comidas que SÍ pueden llevar grasa
    const nonPWCount = Math.max(numMeals - (postWorkoutIdx >= 0 ? 1 : 0), 1);
    const fatPerNonPWMeal = Math.round(grasas / nonPWCount);

    // Inicializar comidas - Frutas solo en comida 1 y 2 (Desayuno y Almuerzo/Media mañana)
    const mealNames = ['Desayuno', 'Almuerzo', 'Merienda', 'Cena', 'Snack 1', 'Snack 2'];
    const fruitMeals = [0, 1]; // índices de comidas con fruta
    
    const initialMeals = Array(numMeals).fill(null).map((_, i) => {
      const isPW = i === postWorkoutIdx;
      return {
        name: isPW ? `${mealNames[i]} (Post-Entreno)` : mealNames[i],
        isPostWorkout: isPW,
        hasVegetables: !isPW, // Post-entreno sin vegetales por defecto
        targets: {
          proteinas: protPerMeal,
          carbohidratos: carbPerMeal,
          grasas: isPW ? 0 : fatPerNonPWMeal
        },
        foods: {
          proteina: { foodId: '', grams: 0 },
          carbohidrato: { foodId: '', grams: 0 },
          grasa: { foodId: '', grams: 0, disabled: isPW, message: isPW ? 'Comida post-entreno: sin grasa' : null },
          vegetal: { foodId: '', grams: 0 }
        },
        hasFruit: fruitMeals.includes(i) && !isPW, // Post-entreno sin fruta por defecto
        fruit: { foodId: '', grams: 0 }
      };
    });

    setNutritionPlan({ calorias, proteinas, carbohidratos, grasas, mantenimiento, objetivo });
    setMeals(initialMeals);
    setStep(2);
  };

  const handleFoodSelect = (mealIndex, category, foodId) => {
    const food = getFoodById(category, foodId);
    if (!food && foodId) return;

    const meal = meals[mealIndex];

    // Bloquear grasa en comida post-entreno
    if (category === 'grasa' && meal?.isPostWorkout) {
      return;
    }
    
    setMeals(prev => {
      const newMeals = [...prev];
      const currentMeal = { ...newMeals[mealIndex] };
      
      if (category === 'fruta') {
        if (!foodId) {
          currentMeal.fruit = { foodId: '', grams: 0 };
        } else {
          // Frutas: calcular para ~15g de carbohidratos
          const portion = calculatePortionForSelect(food, 15, 'carbohidratos');
          currentMeal.fruit = { foodId, grams: portion.displayGrams };
        }
      } else if (category === 'vegetal') {
        currentMeal.foods.vegetal = { foodId, grams: foodId ? 150 : 0 }; // Sugerido 100-200g
      } else if (category === 'proteina') {
        if (!foodId) {
          currentMeal.foods.proteina = { foodId: '', grams: 0 };
          if (!currentMeal.isPostWorkout) {
            // Resetear grasa cuando se quita proteína
            currentMeal.foods.grasa = { foodId: '', grams: 0, disabled: false, message: null };
          }
        } else {
          // Gramos exactos para cubrir la meta de proteína de esta comida
          const grams = food.proteinas > 0 ? Math.round((meal.targets.proteinas / food.proteinas) * 100) : 0;
          currentMeal.foods.proteina = { foodId, grams };
          // Info visual: si la proteína ya aporta suficiente grasa, marcar el bloque de grasa
          const fatFromProt = (food.grasas * grams) / 100;
          if (!currentMeal.isPostWorkout && meal.targets.grasas > 0 && fatFromProt >= meal.targets.grasas) {
            currentMeal.foods.grasa = { ...currentMeal.foods.grasa, foodId: '', grams: 0, disabled: true, message: 'La proteína aporta suficiente grasa' };
          } else if (!currentMeal.isPostWorkout) {
            currentMeal.foods.grasa = { ...currentMeal.foods.grasa, disabled: false, message: null };
          }
        }
      } else if (category === 'carbohidrato') {
        if (!foodId) {
          currentMeal.foods.carbohidrato = { foodId: '', grams: 0 };
        } else {
          // Descontar carbos aportados por la fruta si aplica
          const fruitCarbs = currentMeal.hasFruit && currentMeal.fruit.foodId ? 15 : 0;
          const targetCarbs = Math.max(meal.targets.carbohidratos - fruitCarbs, 0);
          const grams = food.carbohidratos > 0 ? Math.round((targetCarbs / food.carbohidratos) * 100) : 0;
          currentMeal.foods.carbohidrato = { foodId, grams };
        }
      } else if (category === 'grasa') {
        if (!foodId) {
          currentMeal.foods.grasa = { ...currentMeal.foods.grasa, foodId: '', grams: 0 };
        } else {
          // Gramos exactos para cubrir la grasa faltante (después de descontar la aportada por proteína)
          const proteinFood = getFoodById('proteina', currentMeal.foods.proteina.foodId);
          const fatFromProt = proteinFood && currentMeal.foods.proteina.grams > 0
            ? (proteinFood.grasas * currentMeal.foods.proteina.grams) / 100
            : 0;
          const remainingFat = Math.max(currentMeal.targets.grasas - fatFromProt, 0);
          const grams = food.grasas > 0 ? Math.round((remainingFat / food.grasas) * 100) : 0;
          currentMeal.foods.grasa = { foodId, grams, disabled: false, message: null };
        }
      }
      
      newMeals[mealIndex] = currentMeal;
      return newMeals;
    });
  };

  const savePlan = async () => {
    const token = localStorage.getItem('session_token');
    setSaving(true);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/challenges/${challengeId}/nutrition-form`,
        {
          ...formData,
          comida_post_entreno: formData.comida_post_entreno,
          nutrition_plan: nutritionPlan,
          meals: meals
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('¡Plan nutricional guardado!');
      setSavedPlan({ form_data: { ...formData, nutrition_plan: nutritionPlan, meals } });
      setShowSavedPlan(true);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const calculateMealMacros = (meal) => {
    let totalProt = 0, totalCarb = 0, totalFat = 0, totalCal = 0;

    ['proteina', 'carbohidrato', 'grasa'].forEach(cat => {
      const { foodId, grams } = meal.foods[cat];
      const food = getFoodById(cat, foodId);
      if (food && grams > 0) {
        const mult = grams / 100;
        totalProt += food.proteinas * mult;
        totalCarb += food.carbohidratos * mult;
        totalFat += food.grasas * mult;
        totalCal += food.calorias * mult;
      }
    });

    // Agregar fruta si aplica
    if (meal.hasFruit && meal.fruit.foodId) {
      const food = getFoodById('fruta', meal.fruit.foodId);
      if (food && meal.fruit.grams > 0) {
        const mult = meal.fruit.grams / 100;
        totalProt += food.proteinas * mult;
        totalCarb += food.carbohidratos * mult;
        totalFat += food.grasas * mult;
        totalCal += food.calorias * mult;
      }
    }

    return {
      proteinas: Math.round(totalProt),
      carbohidratos: Math.round(totalCarb),
      grasas: Math.round(totalFat),
      calorias: Math.round(totalCal)
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  // Verificar si el challenge ha iniciado y/o terminado
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = challenge?.start_date ? new Date(challenge.start_date) : null;
  const endDate = challenge?.end_date ? new Date(challenge.end_date) : null;
  if (startDate) startDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(23, 59, 59, 999);
  
  const hasStarted = !startDate || today >= startDate;
  const hasEnded = endDate && today > endDate;

  // Si NO ha iniciado el challenge - mostrar bloqueo
  if (!hasStarted) {
    const diffTime = startDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return (
      <div className="min-h-screen bg-[#09090b] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(`/challenge/${challengeId}`)}
            className="inline-flex items-center text-zinc-400 hover:text-pink-500 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Challenge
          </button>

          <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-pink-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Plan Nutricional Bloqueado</h1>
            <p className="text-zinc-400 text-lg mb-2">
              El Plan Nutricional estará disponible cuando inicie el challenge.
            </p>
            <p className="text-pink-400 font-bold text-xl mb-6">
              Faltan {diffDays} {diffDays === 1 ? 'día' : 'días'} para comenzar
            </p>
            <p className="text-zinc-500">
              El contenido se desbloqueará el{' '}
              <span className="text-pink-400 font-medium">
                {startDate.toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Si el challenge YA TERMINÓ
  if (hasEnded) {
    return (
      <div className="min-h-screen bg-[#09090b] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(`/challenge/${challengeId}`)}
            className="inline-flex items-center text-zinc-400 hover:text-pink-500 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Challenge
          </button>

          <div className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 border border-pink-500/30 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">¡Gracias por participar!</h1>
            <p className="text-zinc-300 text-lg mb-6 max-w-xl mx-auto">
              Este challenge ha finalizado. Te esperamos en nuestro próximo challenge.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/programs')}
                className="bg-pink-600 hover:bg-pink-500 text-white px-6 py-3 rounded-full font-bold transition-colors"
              >
                Ver Programas
              </button>
              <button
                onClick={() => navigate('/challenges')}
                className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-full font-bold transition-colors"
              >
                Próximos Challenges
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar plan guardado
  if (showSavedPlan && savedPlan) {
    const saved = savedPlan.form_data;
    return (
      <div className="min-h-screen bg-[#09090b] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(`/challenge/${challengeId}`)}
            className="inline-flex items-center text-zinc-400 hover:text-pink-500 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Challenge
          </button>

          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <h1 className="text-2xl font-bold text-white">Tu Plan Nutricional Guardado</h1>
            </div>
            
            {saved.nutrition_plan && (
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-black/30 rounded-xl p-3">
                  <div className="text-2xl font-bold text-pink-500">{saved.nutrition_plan.calorias}</div>
                  <div className="text-xs text-zinc-400">Calorías/día</div>
                </div>
                <div className="bg-black/30 rounded-xl p-3">
                  <div className="text-2xl font-bold text-blue-400">{saved.nutrition_plan.proteinas}g</div>
                  <div className="text-xs text-zinc-400">Proteínas</div>
                </div>
                <div className="bg-black/30 rounded-xl p-3">
                  <div className="text-2xl font-bold text-yellow-400">{saved.nutrition_plan.carbohidratos}g</div>
                  <div className="text-xs text-zinc-400">Carbos</div>
                </div>
                <div className="bg-black/30 rounded-xl p-3">
                  <div className="text-2xl font-bold text-green-400">{saved.nutrition_plan.grasas}g</div>
                  <div className="text-xs text-zinc-400">Grasas</div>
                </div>
              </div>
            )}
          </div>

          {/* Mostrar comidas guardadas */}
          {saved.meals && saved.meals.map((meal, idx) => {
            const mm = calculateMealMacros(meal);
            return (
            <div key={idx} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 mb-4">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2 flex-wrap">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${meal.isPostWorkout ? 'bg-cyan-500' : 'bg-pink-500'}`}>{idx + 1}</span>
                {meal.name}
                {meal.isPostWorkout && (
                  <span className="text-xs bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 px-2 py-0.5 rounded-full font-medium">
                    🏋️ Post-Entreno
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {meal.foods.proteina.foodId && (
                  <div className="bg-blue-500/10 rounded-lg p-2">
                    <div className="text-blue-400 font-medium">{getFoodById('proteina', meal.foods.proteina.foodId)?.nombre}</div>
                    <div className="text-zinc-400">{meal.foods.proteina.grams}g</div>
                  </div>
                )}
                {meal.foods.carbohidrato.foodId && (
                  <div className="bg-yellow-500/10 rounded-lg p-2">
                    <div className="text-yellow-400 font-medium">{getFoodById('carbohidrato', meal.foods.carbohidrato.foodId)?.nombre}</div>
                    <div className="text-zinc-400">{meal.foods.carbohidrato.grams}g</div>
                  </div>
                )}
                {meal.foods.grasa.foodId && !meal.foods.grasa.disabled && (
                  <div className="bg-green-500/10 rounded-lg p-2">
                    <div className="text-green-400 font-medium">{getFoodById('grasa', meal.foods.grasa.foodId)?.nombre}</div>
                    <div className="text-zinc-400">{meal.foods.grasa.grams}g</div>
                  </div>
                )}
                {meal.foods.grasa.disabled && (
                  <div className="bg-green-500/10 rounded-lg p-2">
                    <div className="text-green-400 font-medium flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Sin grasa adicional
                    </div>
                    <div className="text-zinc-500 text-xs">
                      {meal.isPostWorkout ? 'Comida post-entreno' : 'La proteína aporta suficiente'}
                    </div>
                  </div>
                )}
                {meal.hasFruit && meal.fruit.foodId && (
                  <div className="bg-orange-500/10 rounded-lg p-2">
                    <div className="text-orange-400 font-medium">{getFoodById('fruta', meal.fruit.foodId)?.nombre}</div>
                    <div className="text-zinc-400">{meal.fruit.grams}g</div>
                  </div>
                )}
              </div>
              
              {/* Vegetales - solo si hasVegetables !== false y no es post-entreno */}
              {(meal.hasVegetables !== false && !meal.isPostWorkout) && (
                <div className="mt-3 bg-emerald-500/10 rounded-lg p-2">
                  <div className="text-emerald-400 text-sm">🥦 Vegetales: Libre consumo (Sugerido 100-200g)</div>
                </div>
              )}

              {/* Resumen calórico de la comida */}
              <div className="mt-3 pt-3 border-t border-zinc-700 flex justify-end gap-4 text-xs flex-wrap">
                <span className="text-pink-400 font-medium">{mm.calorias} kcal</span>
                <span className="text-blue-400">{mm.proteinas}g P</span>
                <span className="text-yellow-400">{mm.carbohidratos}g C</span>
                <span className="text-green-400">{mm.grasas}g G</span>
              </div>
            </div>
            );
          })}

          {/* Total del día */}
          {saved.meals && (() => {
            const totals = saved.meals.reduce((acc, m) => {
              const mm = calculateMealMacros(m);
              acc.cal += mm.calorias; acc.p += mm.proteinas; acc.c += mm.carbohidratos; acc.g += mm.grasas;
              return acc;
            }, { cal: 0, p: 0, c: 0, g: 0 });
            return (
              <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-4 mb-4" data-testid="saved-daily-totals">
                <h3 className="text-white font-bold mb-2 text-sm">Total del día</h3>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div><div className="text-pink-400 font-bold text-xl">{totals.cal}</div><div className="text-xs text-zinc-500">kcal</div></div>
                  <div><div className="text-blue-400 font-bold text-xl">{totals.p}g</div><div className="text-xs text-zinc-500">Prot</div></div>
                  <div><div className="text-yellow-400 font-bold text-xl">{totals.c}g</div><div className="text-xs text-zinc-500">Carb</div></div>
                  <div><div className="text-green-400 font-bold text-xl">{totals.g}g</div><div className="text-xs text-zinc-500">Grasa</div></div>
                </div>
              </div>
            );
          })()}

          <div className="flex flex-col md:flex-row gap-3 no-print">
            <Button
              onClick={() => window.print()}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full py-5"
              data-testid="challenge-nutrition-download-btn"
            >
              📥 Descargar Plan (PDF)
            </Button>
            <Button
              onClick={() => { setShowSavedPlan(false); setStep(1); }}
              className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded-full py-5"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Crear Nuevo Plan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const goalInfo = GOAL_LABELS[challenge?.nutrition_goal] || GOAL_LABELS.mantener;
  const GoalIcon = goalInfo.icon;

  return (
    <div className="min-h-screen bg-[#09090b] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(`/challenge/${challengeId}`)}
            className="inline-flex items-center text-zinc-400 hover:text-pink-500 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Challenge
          </button>
          
          <h1 className="text-3xl font-bold text-white mb-2">
            Plan Nutricional - {challenge?.title || challenge?.name}
          </h1>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${goalInfo.bg}`}>
            <GoalIcon className={`w-5 h-5 ${goalInfo.color}`} />
            <span className={`font-medium ${goalInfo.color}`}>{goalInfo.label}</span>
          </div>
        </div>

        {/* Step 1: Datos */}
        {step === 1 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-6 h-6 text-pink-500" />
              <h2 className="text-xl font-bold text-white">Tus Datos</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-zinc-300 mb-2">Peso (kg)</label>
                <Input
                  type="number"
                  value={formData.peso}
                  onChange={(e) => setFormData(prev => ({ ...prev, peso: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ej: 65"
                />
              </div>
              <div>
                <label className="block text-zinc-300 mb-2">Estatura (cm)</label>
                <Input
                  type="number"
                  value={formData.estatura}
                  onChange={(e) => setFormData(prev => ({ ...prev, estatura: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ej: 165"
                />
              </div>
              <div>
                <label className="block text-zinc-300 mb-2">Edad</label>
                <Input
                  type="number"
                  value={formData.edad}
                  onChange={(e) => setFormData(prev => ({ ...prev, edad: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ej: 28"
                />
              </div>
              <div>
                <label className="block text-zinc-300 mb-2">Sexo</label>
                <select
                  value={formData.sexo}
                  onChange={(e) => setFormData(prev => ({ ...prev, sexo: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3"
                >
                  <option value="mujer">Mujer</option>
                  <option value="hombre">Hombre</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-300 mb-2">Nivel de Actividad</label>
                <select
                  value={formData.nivel_actividad}
                  onChange={(e) => setFormData(prev => ({ ...prev, nivel_actividad: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3"
                >
                  <option value="sedentario">Sedentario (poco ejercicio)</option>
                  <option value="ligero">Ligero (1-3 días/semana)</option>
                  <option value="moderado">Moderado (3-5 días/semana)</option>
                  <option value="activo">Activo (6-7 días/semana)</option>
                  <option value="muy_activo">Muy Activo (2x al día)</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-300 mb-2">Comidas al Día</label>
                <select
                  value={formData.numero_comidas}
                  onChange={(e) => {
                    const newNum = e.target.value;
                    setFormData(prev => {
                      const pw = parseInt(prev.comida_post_entreno);
                      const resetPW = pw > parseInt(newNum) ? '' : prev.comida_post_entreno;
                      return { ...prev, numero_comidas: newNum, comida_post_entreno: resetPW };
                    });
                  }}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3"
                  data-testid="challenge-nutrition-num-comidas"
                >
                  <option value="3">3 comidas</option>
                  <option value="4">4 comidas</option>
                  <option value="5">5 comidas</option>
                  <option value="6">6 comidas</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-300 mb-2">Comida Post-Entreno (opcional)</label>
                <select
                  value={formData.comida_post_entreno}
                  onChange={(e) => setFormData(prev => ({ ...prev, comida_post_entreno: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3"
                  data-testid="challenge-nutrition-post-workout"
                >
                  <option value="">Ninguna</option>
                  {Array.from({ length: parseInt(formData.numero_comidas || '0') }, (_, i) => i + 1).map(n => (
                    <option key={n} value={String(n)}>Comida {n}</option>
                  ))}
                </select>
                <p className="text-xs text-zinc-500 mt-1">La comida post-entreno no llevará grasa</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-zinc-300 mb-2">Alergias (opcional)</label>
                <Input
                  type="text"
                  value={formData.alergias}
                  onChange={(e) => setFormData(prev => ({ ...prev, alergias: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ej: maní, mariscos (separados por coma)"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-zinc-300 mb-2">Alimentos que NO te gustan (opcional)</label>
                <Input
                  type="text"
                  value={formData.alimentos_no_gustan}
                  onChange={(e) => setFormData(prev => ({ ...prev, alimentos_no_gustan: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ej: hígado, brócoli (separados por coma)"
                />
              </div>
            </div>

            <Button
              onClick={calculateNutrition}
              className="w-full mt-6 bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calcular Mi Plan
            </Button>
          </div>
        )}

        {/* Step 2: Crear menú */}
        {step === 2 && nutritionPlan && (
          <div className="space-y-6">
            {/* Resumen de macros */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-6 text-white">
              <h2 className="text-xl font-bold mb-4">Tus Macros Diarios</h2>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold">{nutritionPlan.calorias}</div>
                  <div className="text-white/70">kcal</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{nutritionPlan.proteinas}g</div>
                  <div className="text-white/70">Proteínas</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{nutritionPlan.carbohidratos}g</div>
                  <div className="text-white/70">Carbos</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">{nutritionPlan.grasas}g</div>
                  <div className="text-white/70">Grasas</div>
                </div>
              </div>
            </div>

            {/* Nota sobre grasa inteligente */}
            <div className="bg-green-900/30 border border-green-500/50 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-300 text-sm font-medium">
                    💡 Sistema de Grasa Inteligente
                  </p>
                  <p className="text-green-400/80 text-xs mt-1">
                    El sistema calcula automáticamente si necesitas agregar grasa adicional. 
                    Si la proteína seleccionada ya aporta suficiente grasa, el selector se desactivará.
                  </p>
                </div>
              </div>
            </div>

            {/* Comidas */}
            {meals.map((meal, mealIndex) => {
              const mealMacros = calculateMealMacros(meal);
              // La grasa está deshabilitada si es post-entreno o si la proteína ya cubre la meta
              const fatDisabled = !!meal.foods.grasa.disabled;
              const proteinFood = getFoodById('proteina', meal.foods.proteina.foodId);
              const fatFromProt = proteinFood && meal.foods.proteina.grams > 0
                ? (proteinFood.grasas * meal.foods.proteina.grams) / 100
                : 0;
              const remainingFatForMeal = Math.max(meal.targets.grasas - fatFromProt, 0);
              
              return (
                <div key={mealIndex} className={`bg-zinc-900 border rounded-2xl p-5 ${meal.isPostWorkout ? 'border-cyan-500/50' : 'border-zinc-800'}`}>
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${meal.isPostWorkout ? 'bg-cyan-500' : 'bg-pink-500'}`}>
                        {mealIndex + 1}
                      </span>
                      {meal.name}
                      {meal.isPostWorkout && (
                        <span className="text-xs bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 px-2 py-0.5 rounded-full font-medium">
                          🏋️ Post-Entreno · sin grasa
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-2">
                      {/* Reordenar */}
                      <button
                        type="button"
                        disabled={mealIndex === 0}
                        onClick={() => moveMeal(mealIndex, -1)}
                        className="p-1 rounded bg-zinc-800 hover:bg-zinc-700 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`move-meal-up-${mealIndex}`}
                        title="Subir comida"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        disabled={mealIndex === meals.length - 1}
                        onClick={() => moveMeal(mealIndex, 1)}
                        className="p-1 rounded bg-zinc-800 hover:bg-zinc-700 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`move-meal-down-${mealIndex}`}
                        title="Bajar comida"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <div className="text-xs text-zinc-400 ml-2">
                        Meta: {meal.targets.proteinas}g P / {meal.targets.carbohidratos}g C / {meal.targets.grasas}g G
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Proteína */}
                    <div className="bg-zinc-800 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-blue-400 font-medium text-sm">🥩 Proteína</span>
                        <span className="text-xs text-zinc-500">Meta: {meal.targets.proteinas}g</span>
                      </div>
                      <select
                        value={meal.foods.proteina.foodId}
                        onChange={(e) => handleFoodSelect(mealIndex, 'proteina', e.target.value)}
                        className="w-full bg-zinc-700 border-0 text-white rounded-lg p-2 text-sm"
                      >
                        <option value="">Seleccionar...</option>
                        {filterFoods(FOOD_DATABASE.proteinas).map(f => {
                          const portion = calculatePortionForSelect(f, meal.targets.proteinas, 'proteinas');
                          return (
                            <option key={f.id} value={f.id}>
                              {f.nombre} - {portion.displayGrams}g ({f.medicion})
                            </option>
                          );
                        })}
                      </select>
                      {meal.foods.proteina.grams > 0 && (
                        <div className="mt-2 flex justify-between items-center flex-wrap gap-2">
                          <GramEditor mealIndex={mealIndex} category="proteina" grams={meal.foods.proteina.grams} onSet={setMealFoodGrams} onAdjust={adjustMealFoodGrams} />
                          {proteinFood && (
                            <span className="text-zinc-400 text-xs">
                              Aporta ~{Math.round((proteinFood.grasas * meal.foods.proteina.grams) / 100)}g grasa
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Carbohidrato */}
                    <div className="bg-zinc-800 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-yellow-400 font-medium text-sm">🍚 Carbohidrato</span>
                        <span className="text-xs text-zinc-500">
                          Meta: {meal.targets.carbohidratos}g total
                          {meal.hasFruit && meal.fruit.foodId && ' (incluye ~15g fruta)'}
                        </span>
                      </div>
                      <select
                        value={meal.foods.carbohidrato.foodId}
                        onChange={(e) => handleFoodSelect(mealIndex, 'carbohidrato', e.target.value)}
                        className="w-full bg-zinc-700 border-0 text-white rounded-lg p-2 text-sm"
                      >
                        <option value="">Seleccionar...</option>
                        {filterFoods(FOOD_DATABASE.carbohidratos).map(f => {
                          const fruitCarbs = meal.hasFruit && meal.fruit.foodId ? 15 : 0;
                          const adjustedTarget = Math.max(meal.targets.carbohidratos - fruitCarbs, 5);
                          const portion = calculatePortionForSelect(f, adjustedTarget, 'carbohidratos');
                          return (
                            <option key={f.id} value={f.id}>
                              {f.nombre} - {portion.displayGrams}g ({f.medicion})
                            </option>
                          );
                        })}
                      </select>
                      {meal.foods.carbohidrato.grams > 0 && (
                        <div className="mt-2">
                          <GramEditor mealIndex={mealIndex} category="carbohidrato" grams={meal.foods.carbohidrato.grams} onSet={setMealFoodGrams} onAdjust={adjustMealFoodGrams} />
                        </div>
                      )}
                    </div>

                    {/* Grasa Inteligente */}
                    <div className={`rounded-xl p-3 ${fatDisabled ? 'bg-green-900/30 border border-green-500/30' : 'bg-zinc-800'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-green-400 font-medium text-sm">🥑 Grasa Saludable</span>
                        <span className="text-xs text-zinc-500">
                          {fatDisabled
                            ? '✓ Completa'
                            : `Faltante: ${Math.round(remainingFatForMeal)}g`}
                        </span>
                      </div>
                      
                      {fatDisabled ? (
                        <div className="flex items-center gap-2 text-green-400 p-2 bg-green-900/20 rounded-lg">
                          <CheckCircle className="w-5 h-5" />
                          <div>
                            <span className="font-medium text-sm">
                              {meal.isPostWorkout ? 'Comida post-entreno' : 'No necesitas grasa adicional'}
                            </span>
                            <p className="text-xs text-green-500">
                              {meal.foods.grasa.message || 'La proteína aporta suficiente grasa'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <select
                            value={meal.foods.grasa.foodId}
                            onChange={(e) => handleFoodSelect(mealIndex, 'grasa', e.target.value)}
                            className="w-full bg-zinc-700 border-0 text-white rounded-lg p-2 text-sm"
                          >
                            <option value="">Seleccionar...</option>
                            {filterFoods(FOOD_DATABASE.grasas).map(f => {
                              const portion = calculatePortionForSelect(f, remainingFatForMeal, 'grasas');
                              return (
                                <option key={f.id} value={f.id}>
                                  {f.nombre} - {Math.round(portion.grams)}g
                                </option>
                              );
                            })}
                          </select>
                          {meal.foods.grasa.grams > 0 && (
                            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                              <GramEditor mealIndex={mealIndex} category="grasa" grams={meal.foods.grasa.grams} onSet={setMealFoodGrams} onAdjust={adjustMealFoodGrams} />
                              {/* Mover grasa a otra comida */}
                              <select
                                value=""
                                onChange={(e) => {
                                  const to = parseInt(e.target.value);
                                  if (!isNaN(to)) moveFoodToMeal(mealIndex, 'grasa', to);
                                }}
                                className="text-xs bg-zinc-700 border-0 text-white rounded px-2 py-1"
                                data-testid={`move-fat-select-${mealIndex}`}
                              >
                                <option value="">Mover grasa a...</option>
                                {meals.map((m, idx) => (
                                  idx !== mealIndex && !m.isPostWorkout ? (
                                    <option key={idx} value={idx}>Comida {idx + 1}</option>
                                  ) : null
                                ))}
                              </select>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Vegetal - opcional, no en post-entreno */}
                    {meal.hasVegetables ? (
                      <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-emerald-400 font-medium text-sm">🥦 Vegetal</span>
                          <button
                            type="button"
                            onClick={() => toggleVegetables(mealIndex)}
                            className="text-xs text-emerald-400 hover:text-red-400 underline"
                            data-testid={`toggle-veg-${mealIndex}`}
                          >
                            Quitar
                          </button>
                        </div>
                        <p className="text-emerald-300 text-sm">
                          Sugerido: 100-200g (no cuenta en macros)
                        </p>
                        <p className="text-emerald-400/70 text-xs mt-1">
                          ¡Agrega todos los vegetales que quieras!
                        </p>
                      </div>
                    ) : (
                      !meal.isPostWorkout && (
                        <div className="bg-zinc-800/50 border border-dashed border-zinc-700 rounded-xl p-3 flex items-center justify-between">
                          <span className="text-zinc-500 text-sm">Sin vegetales</span>
                          <button
                            type="button"
                            onClick={() => toggleVegetables(mealIndex)}
                            className="text-xs text-emerald-400 hover:text-emerald-300 underline"
                            data-testid={`toggle-veg-${mealIndex}`}
                          >
                            🥦 Agregar
                          </button>
                        </div>
                      )
                    )}

                    {/* Fruta - solo en comidas específicas */}
                    {meal.hasFruit && (
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 md:col-span-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-orange-400 font-medium text-sm">🍎 Fruta (~15g carbos)</span>
                          <span className="text-xs text-zinc-500">Cuenta como carbohidratos</span>
                        </div>
                        <select
                          value={meal.fruit.foodId}
                          onChange={(e) => handleFoodSelect(mealIndex, 'fruta', e.target.value)}
                          className="w-full bg-zinc-700 border-0 text-white rounded-lg p-2 text-sm"
                        >
                          <option value="">Seleccionar fruta...</option>
                          {filterFoods(FOOD_DATABASE.frutas).map(f => {
                            const portion = calculatePortionForSelect(f, 15, 'carbohidratos');
                            return (
                              <option key={f.id} value={f.id}>
                                {f.nombre} - {portion.displayGrams}g ({f.carbohidratos}g carbs/100g)
                              </option>
                            );
                          })}
                        </select>
                        {meal.fruit.grams > 0 && (
                          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                            <GramEditor mealIndex={mealIndex} category="fruta" grams={meal.fruit.grams} colorClass="text-orange-300" onSet={setMealFoodGrams} onAdjust={adjustMealFoodGrams} />
                            <select
                              value=""
                              onChange={(e) => {
                                const to = parseInt(e.target.value);
                                if (!isNaN(to)) moveFoodToMeal(mealIndex, 'fruta', to);
                              }}
                              className="text-xs bg-zinc-700 border-0 text-white rounded px-2 py-1"
                              data-testid={`move-fruit-select-${mealIndex}`}
                            >
                              <option value="">Mover fruta a...</option>
                              {meals.map((m, idx) => (
                                idx !== mealIndex ? (
                                  <option key={idx} value={idx}>Comida {idx + 1}</option>
                                ) : null
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Resumen de la comida */}
                  <div className="mt-4 pt-3 border-t border-zinc-700 flex justify-end gap-4 text-xs">
                    <span className="text-zinc-400">Total comida:</span>
                    <span className="text-pink-400">{mealMacros.calorias} kcal</span>
                    <span className="text-blue-400">{mealMacros.proteinas}g P</span>
                    <span className="text-yellow-400">{mealMacros.carbohidratos}g C</span>
                    <span className="text-green-400">{mealMacros.grasas}g G</span>
                  </div>
                </div>
              );
            })}

            {/* Notas */}
            <div className="bg-amber-900/30 border border-amber-500/50 rounded-xl p-4">
              <h3 className="font-bold text-amber-300 mb-2 text-sm">📌 Reglas de Medición</h3>
              <ul className="text-amber-400/90 text-xs space-y-1">
                <li>• <strong>Proteínas:</strong> Peso en CRUDO</li>
                <li>• <strong>Carbohidratos:</strong> Arroz, pasta, papa = COCIDO | Avena, crema de arroz = CRUDO (polvo)</li>
                <li>• <strong>Frutas:</strong> Peso en CRUDO. Cuentan como carbohidratos</li>
                <li>• <strong>Vegetales:</strong> Libres (sugerido 100-200g)</li>
              </ul>
            </div>

            {/* Resumen Total Diario */}
            {(() => {
              const totals = meals.reduce((acc, m) => {
                const mm = calculateMealMacros(m);
                acc.cal += mm.calorias;
                acc.p += mm.proteinas;
                acc.c += mm.carbohidratos;
                acc.g += mm.grasas;
                return acc;
              }, { cal: 0, p: 0, c: 0, g: 0 });
              const overCal = totals.cal - nutritionPlan.calorias;
              const isOver = overCal > 50;
              return (
                <div className={`rounded-2xl p-5 border ${isOver ? 'bg-red-900/30 border-red-500/40' : 'bg-zinc-900 border-zinc-700'}`} data-testid="daily-totals">
                  <h3 className={`font-bold mb-3 ${isOver ? 'text-red-300' : 'text-white'}`}>
                    {isOver ? '⚠️ Total Diario (excede la meta)' : '✅ Total Diario'}
                  </h3>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div>
                      <div className={`text-2xl font-bold ${isOver ? 'text-red-400' : 'text-pink-400'}`}>{totals.cal}</div>
                      <div className="text-xs text-zinc-400">kcal (meta {nutritionPlan.calorias})</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-400">{totals.p}g</div>
                      <div className="text-xs text-zinc-400">Prot (meta {nutritionPlan.proteinas}g)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-400">{totals.c}g</div>
                      <div className="text-xs text-zinc-400">Carb (meta {nutritionPlan.carbohidratos}g)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-400">{totals.g}g</div>
                      <div className="text-xs text-zinc-400">Grasa (meta {nutritionPlan.grasas}g)</div>
                    </div>
                  </div>
                  {isOver && (
                    <p className="text-red-300 text-xs mt-3">
                      El total supera tu meta diaria en {overCal} kcal. Prueba una proteína más magra
                      (ej. pechuga de pollo, clara de huevo, atún) para reducir grasa aportada.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Botón Guardar */}
            <Button
              onClick={savePlan}
              disabled={saving}
              className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 text-lg"
              data-testid="challenge-nutrition-save-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Guardar Mi Plan Nutricional
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengeNutrition;
